// API
import InventoryApi from '@/api/InventoryApi';

// Utils
import AudioUtil, { soundNames } from '@/utils/AudioUtil';
import ErrorUtil from '@/utils/ErrorUtil';
import ScanUtil from '@/utils/ScanUtil';
import { InventoryManagementApi } from '@/api/InventoryManagementApi';

// Mutations
export const mutationTypes = {
    PUSH_SHELVED_ITEM: 'PUSH_SHELVED_ITEM',
    PUSH_SCANNED_ITEM: 'PUSH_SCANNED_ITEM',
    REMOVE_SCANNED_ITEM: 'REMOVE_SCANNED_ITEM',
    RESET_SCANNED_ITEMS: 'RESET_SCANNED_ITEMS',
    RESET_SHELVED_ITEMS: 'RESET_SHELVED_ITEMS',
    RESET_LOCATOR: 'RESET_LOCATOR',
    SET_SCAN_STRING: 'SET_SCAN_STRING',
    SET_SCANNED_ITEM: 'SET_SCANNED_ITEM',
    SET_LOCATOR: 'SET_LOCATOR',
    SET_SHELF_IN_FLIGHT: 'SET_SHELF_IN_FLIGHT',
};

export const actionTypes = {
    getSerialItemInfo: 'getSerialItemInfo',
    processScan: 'processScan',
    scanSerial: 'scanSerial',
    scanShelf: 'scanShelf',
    reset: 'reset',
};

export default {
    namespaced: true,
    state: {
        /**
         * @type { String }
         */
        scanString: '',
        /**
         * @type { Object }
         */
        locator: {},
        /**
         * @type { Object[] }
         */
        scannedItems: [],
        /**
         * @type { Object[] }
         */
        shelvedItems: [],
        /**
         * @type { Boolean }
         */
        shelfInFlight: false,
    },
    getters: {
        /**
         * @param { Object[] } scannedItems - The scanned items
         * @returns { Boolean } - true for any failed item, false for all success
         */
        hasFailedItems: ( { scannedItems } ) => scannedItems.some( ( item ) => item.status === 'error' ),
    },
    actions: {
        [ actionTypes.reset ]( { commit } ) {
            commit( mutationTypes.SET_SCAN_STRING, { scanString: '' } );
            commit( mutationTypes.SET_LOCATOR, { locator: {} } );
            commit( mutationTypes.RESET_SCANNED_ITEMS );
            commit( mutationTypes.RESET_SHELVED_ITEMS );
            commit( mutationTypes.SET_SHELF_IN_FLIGHT, { shelfInFlight: false } );
        },

        async [ actionTypes.processScan ]( {
            dispatch,
            commit,
            state,
        } ) {
            const scan = state.scanString;
            if ( ScanUtil.isEmptyString( scan ) ) { return; }
            if ( ScanUtil.isSerialRef( scan ) ) {
                commit( mutationTypes.SET_SCAN_STRING, { scanString: '' } );
                await dispatch( actionTypes.scanSerial, { scan } );
            } else if ( !ScanUtil.isNumeric( scan ) ) {
                commit( mutationTypes.SET_SCAN_STRING, { scanString: '' } );
                dispatch( 'snackbar', {
                    message: 'Not a valid ID',
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                await AudioUtil.playSound( soundNames.FAILURE_SHORT );
            } else {
                commit( mutationTypes.SET_SCAN_STRING, { scanString: '' } );
                await dispatch( actionTypes.scanShelf, { scan } );
            }
        },
        async scanSerial({
            commit,
            state,
            dispatch,
            rootState,
            rootGetters,
        }, { scan, noBeeps }) {
            if (ScanUtil.hasItem(scan, state.scannedItems)) {
                commit('SET_SCANNED_ITEM', { serial: scan, status: 'loading' });
            } else {
                commit('PUSH_SCANNED_ITEM', {
                    serial: scan,
                    title: '',
                    source: '',
                    status: 'loading',
                });
            }

            // If we have shelf info
            if (state.locator?.ref) {
                const Api = new InventoryManagementApi(rootState.User.baseAuth, rootGetters['Website/locationId']);
                const response = await Api.moveInventory(scan, state.locator.ref);

                if (response instanceof Error) {
                    const { title, source } = await dispatch('getSerialItemInfo', {
                        ref: scan,
                        message: response.message,
                    });
                    // Set our item in an error status
                    commit('SET_SCANNED_ITEM', {
                        serial: scan,
                        title,
                        source,
                        destination: state.locator.name,
                        status: 'error',
                        message: response.message,
                    });
                    if (!noBeeps) {
                        await AudioUtil.playSound(soundNames.FAILURE_SHORT);
                    }
                    return;
                }

                // Update Scanned Item table and item to history
                const item = response[0];
                commit('SET_SCANNED_ITEM', {
                    serial: item.ref,
                    title: item.titleDescription,
                    source: item.preMoveLocator?.name || item.preMoveLocator?.description || item.preMoveLocator?.type,
                    destination: state.locator.name,
                    status: 'complete',
                });
                const shelvedItem = state.scannedItems.find((scannedItem) => scannedItem.serial === scan);

                // But Tyler, What is subId and why would you use the array length? Normally the serial would be the unique key
                // for the table, but since this is history the same serial can be used several times. Unlike the other tables,
                // items cannot be removed from this table. So we can safely use the array length (starting at 0) as the key
                commit('PUSH_SHELVED_ITEM', { ...shelvedItem, subId: state.shelvedItems.length });

                // TODO Update Locator Count once BL-279 is complete: https://treeoflifebooks.atlassian.net/browse/BL-279
                if (!noBeeps) {
                    await AudioUtil.playSound(soundNames.SUCCESS_SHORT);
                }
            } else {
                const { title, source, message } = await dispatch('getSerialItemInfo', { ref: scan });
                commit('SET_SCANNED_ITEM', {
                    serial: scan,
                    title,
                    source,
                    destination: state.locator.name,
                    status: message ? 'error' : 'pending',
                    message,
                });
                if (!noBeeps) {
                    message ? await AudioUtil.playSound(soundNames.FAILURE_SHORT)
                        : await AudioUtil.playSound(soundNames.SUCCESS_SHORT);
                }
            }
        },
        async [ actionTypes.scanShelf ]( {
            commit,
            getters,
            state,
            dispatch,
            rootState,
            rootGetters,
        }, { scan } ) {
            if ( state.locator?.ref ) {
                // New Shelf -- Reset Scanned Items
                commit( mutationTypes.RESET_SCANNED_ITEMS );
            }
            try {
                commit( mutationTypes.SET_SHELF_IN_FLIGHT, { shelfInFlight: true } );
                const { data } = await InventoryApi.getShelfLocatorInformation( {
                    userAccountId: rootState.User.accountId,
                    websiteId: rootGetters[ 'Website/id' ],
                    jwtToken: rootState.User.jwtToken,
                    locatorRef: scan,
                } );
                if ( !data?.locator?.ref ) {
                    // Alert user that no locator
                    throw new Error( 'Locator not found' );
                } else if ( data.locator && ( data.locator.type === 'Delivery' ) ) {
                    throw new Error( 'Cannot Shelve to a Delivery locator' );
                } else {
                    await AudioUtil.playSound( soundNames.SUCCESS_LONG );
                }
                commit( mutationTypes.SET_LOCATOR, { locator: data.locator } );
                if ( state.scannedItems?.length > 0 ) {
                    await Promise.all( state.scannedItems.map( async ( item ) => {
                        await dispatch( actionTypes.scanSerial, { scan: item.serial, noBeeps: true } );
                    } ) );
                    if ( getters.hasFailedItems ) {
                        await AudioUtil.playSound( soundNames.FAILURE_LONG );
                    } else {
                        await AudioUtil.playSound( soundNames.SUCCESS_LONG );
                    }
                }
            } catch ( e ) {
                dispatch( 'snackbar', {
                    message: ErrorUtil.getMessageFromError( e ),
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                await AudioUtil.playSound( soundNames.FAILURE_LONG );
                commit( mutationTypes.SET_LOCATOR, { locator: {} } );
            } finally {
                commit( mutationTypes.SET_SHELF_IN_FLIGHT, { shelfInFlight: false } );
            }
        },
        async [ actionTypes.getSerialItemInfo ]( {
            rootState,
            rootGetters,
            dispatch,
        }, { ref, message } ) {
            // Always ensure ONE error message has been displayed
            let hasDisplayedError = false;
            try {
                const { data } = await InventoryApi.getSerialItemInfo( {
                    userAccountId: rootState.User.accountId,
                    websiteId: rootGetters[ 'Website/id' ],
                    jwtToken: rootState.User.jwtToken,
                    ref,
                } );
                return {
                    title: data.serialItems.list[ 0 ].titleDescription,
                    source: data.serialItems.list[ 0 ].locator?.name,
                };
            } catch ( e ) {
                await dispatch( 'snackbar', {
                    message: message || 'No info found for scanned item',
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                hasDisplayedError = true;
                return {
                    title: '',
                    source: '',
                    message: message || 'No info found for scanned item',
                };
            } finally {
                // We always want to display a message if we haven't shown one already
                if ( message && !hasDisplayedError ) {
                    await dispatch( 'snackbar', {
                        message,
                        type: 'error',
                        duration: 5000,
                    }, { root: true } );
                }
            }
        },
    },
    mutations: {
        [ mutationTypes.SET_SCAN_STRING ]( state, { scanString } ) {
            state.scanString = scanString;
        },
        [ mutationTypes.SET_LOCATOR ]( state, { locator } ) {
            state.locator = locator;
        },
        [ mutationTypes.SET_SHELF_IN_FLIGHT ]( state, { shelfInFlight } ) {
            state.shelfInFlight = shelfInFlight;
        },
        [ mutationTypes.PUSH_SCANNED_ITEM ]( state, scannedItem ) {
            state.scannedItems = [ scannedItem, ...state.scannedItems ];
        },
        [ mutationTypes.PUSH_SHELVED_ITEM ]( state, shelvedItem ) {
            state.shelvedItems = [ shelvedItem, ...state.shelvedItems ];
        },
        [ mutationTypes.SET_SCANNED_ITEM ]( state, {
            serial,
            title,
            source,
            destination,
            status,
            message,
        } ) {
            const itemToChange = state.scannedItems.find( ( item ) => (
                ( item.serial.toString().toLowerCase() === serial.toString().toLowerCase() )
                || ( item.serial.toString().toLowerCase() === `id${ serial.toString().toLowerCase() }` )
            ) );
            itemToChange.serial = serial.toString().toLowerCase().startsWith( 'id' )
                ? serial
                : `ID${ serial }`;
            itemToChange.title = title || itemToChange.title || '';
            itemToChange.source = source || itemToChange.source || '';
            itemToChange.destination = destination || itemToChange.destination || '';
            itemToChange.status = status || itemToChange.status || '';
            itemToChange.message = message || '';
        },
        [ mutationTypes.REMOVE_SCANNED_ITEM ]( state, { serial } ) {
            state.scannedItems = state.scannedItems.filter( ( item ) => item.serial !== serial );
        },
        [ mutationTypes.RESET_SCANNED_ITEMS ]( state ) {
            state.scannedItems = [];
        },
        [ mutationTypes.RESET_SHELVED_ITEMS ]( state ) {
            state.shelvedItems = [];
        },
        [ mutationTypes.RESET_LOCATOR ]( state ) {
            if ( state.scannedItems?.length > 0 ) {
                state.scannedItems = state.scannedItems.map( ( item ) => ( {
                    ...item,
                    source: state.locator?.name,
                    destination: '',
                    status: 'pending',
                } ) );
            }
            state.locator = {};
        },
    },
};
